import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Text,
  Button,
  FormControl,
  ModalBody,
  ModalFooter,
  Input,
  Switch,
} from '@chakra-ui/react';
import { SampleReceiver } from '@texas/api/endpoints/samplesApi';
import {
  CreateTransportRequest,
  transportRequestsApi,
} from '@texas/api/endpoints/transportRequestApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useForm, Controller } from 'react-hook-form';
import { ErrorDetails } from '../alert/ErrorDetails';
import { ErrorLabel } from '../ErrorLabel';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { SharedDisclosureProps } from '../types';
import { transportRequestEvents } from './events';
import { SimpleContact } from '@texas/api/endpoints/contactsApi';
import DatePicker from 'react-datepicker';
import { request } from '@texas/utils/helpers/httpHelpers';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '../users/UserAvatar';
import { FormCounter } from '../form/FormCounter';

interface Props extends SharedDisclosureProps {
  onCreate: (id: number) => void;
  contact: SimpleContact;
}

const maxLength = 200;

export function AddBrandTransportRequestModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered={true}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex gap={2} align="center">
            <Text>{t('transportRequest.create')}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <Inner
          contact={props.contact}
          onCreate={(id) => {
            props.onCreate(id);
            transportRequestEvents.onTransportCreated.dispatch();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  onCreate,
  contact,
}: {
  onCreate: (id: number) => void;
  contact: SimpleContact;
}) {
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    watch,
  } = useForm<CreateTransportRequest>({
    defaultValues: {
      receiverContactId: contact.id,
      receiver: SampleReceiver.Brand,
      requestDispatchDate: new Date(),
      note: '',
    },
  });

  const { request: createRequest, error: createError } = useApiRequest(
    transportRequestsApi.create,
  );

  const performRequest = (data: CreateTransportRequest) =>
    request(createRequest, [data], (id) => onCreate(id));

  return (
    <>
      <form onSubmit={handleSubmit(performRequest)}>
        <ModalBody>
          {createError && <ErrorDetails error={createError} />}

          <Flex gap={4} flexDir="column">
            <FormControl>
              <TexasFormLabel>
                {t('transportRequest.whoWillRecievePackage')}
              </TexasFormLabel>
              <UserAvatar
                name={contact.name}
                email={contact.email}
                label={null}
                showTeams={false}
              />
              <ErrorLabel text={errors.receiverUserId?.message} />
            </FormControl>
            <FormControl>
              <TexasFormLabel>{t('general.note')}</TexasFormLabel>
              <Input {...register('note', { maxLength })} variant="texas" />
              <FormCounter
                length={watch('note').length}
                maxLength={maxLength}
              />
            </FormControl>
            <FormControl gap={2}>
              <Flex
                gap={2}
                alignItems="center"
                justifyContent="space-between"
                p={2}
                bg="texas.bg.800"
                borderRadius="md"
                _light={{
                  bg: 'gray.50',
                }}
              >
                <Text
                  fontSize="sm"
                  color="gray.200"
                  _light={{ color: 'gray.600' }}
                >
                  {t('transportRequest.asap')}
                </Text>

                <Controller
                  name="asap"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      isChecked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </Flex>
            </FormControl>
            {!watch('asap') && (
              <FormControl isRequired={true}>
                <TexasFormLabel>
                  {t('transportRequest.reqDispatchDate')}
                </TexasFormLabel>
                <Controller
                  control={control}
                  name="requestDispatchDate"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      inline={true}
                      calendarStartDay={1}
                    />
                  )}
                />
                <ErrorLabel text={errors.requestDispatchDate?.message} />
              </FormControl>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting || !isValid}
          >
            {t('general.create')}
          </Button>
        </ModalFooter>
      </form>
    </>
  );
}
